import {
  Avatar,
  Col,
  Dropdown,
  Icon,
  Input,
  Modal,
  Row,
  SegmentControl,
  Spinner2,
  Text
} from 'components';
import { useInfiniteScroll } from 'core';
import { Api, theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// Now that the outer Modal's own backdrop is transparent (it sits inside an
// already-open modal), the card needs its own depth cue to read as a
// floating surface rather than content pasted onto the page.
const Card = styled.div`
  width: ${(props) => (props.$inline ? '100%' : 'calc(100% - 40px)')};
  max-height: ${(props) => (props.$inline ? '340px' : '400px')};
  background-color: #fff;
  border-radius: 16px;
  ${(props) =>
    props.$inline
      ? `border: 1px solid ${theme.GREY_10};`
      : 'box-shadow: 0 12px 32px rgba(15, 15, 20, 0.18);'}
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SearchHeader = styled.div`
  padding: 16px 16px 12px;
  border-bottom: 1px solid ${theme.GREY_10};
`;

const ResultList = styled.div`
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 6px 8px;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-radius: 10px;
  cursor: pointer;
  background-color: ${(props) => (props.$selected ? theme.SECONDARY_10 : 'transparent')};
  transition: background-color 0.12s ease;

  &:hover {
    background-color: ${(props) => (props.$selected ? theme.SECONDARY_10 : theme.GREY_10)};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 16px;
`;

let timer;
const SearchUsers = ({
  title,
  handleSelect,
  type,
  selected = [],
  multiple,
  excludeType,
  inline
}) => {
  const [visible, setVisible] = useState(false),
    [loading, setLoading] = useState(false),
    [selectedOpt, setSelectedOpt] = useState(0),
    [text, setText] = useState(''),
    [data, setData] = useState([]),
    [listEndReached, setListEndReached] = useState(false),
    { t } = useTranslation();

  useInfiniteScroll('userList', () => searchUsers());

  useEffect(() => {
    setListEndReached(false);

    searchUsers(true);
  }, [text, type]);

  async function searchUsers(searchNew) {
    const getRecent = multiple && !text.length;

    clearTimeout(timer);

    if (listEndReached && !searchNew) return;

    timer = setTimeout(async () => {
      setLoading(true);

      const res = getRecent
        ? await Api.get('/posts/getRecentTags')
        : await Api.post('/users/search', {
            text,
            type,
            excludeType,
            skip: (!searchNew && data?.length) || 0
          });
      setLoading(false);

      if (!res) return;
      if (!res?.length) return searchNew ? setData([]) : setListEndReached(true);

      setData(searchNew || getRecent ? res : [...data, ...res]);
    }, 300);
  }

  function handleClick(opt) {
    if (!multiple) {
      setVisible(false);
      return handleSelect(opt);
    }

    if (selected.find((x) => x._id === opt._id))
      return handleSelect(selected.filter((x) => x._id !== opt._id));

    handleSelect([opt, ...selected]);
  }

  const visibleResults = data.filter((x) =>
    multiple
      ? !selected.map((x) => x._id).includes(x._id) && x.type === (selectedOpt ? 'athlete' : 'fan')
      : true
  );

  const content = (
    <Card $inline={inline}>
      <SearchHeader>
        <Input
          placeholder={t('common.searchDotDotDot')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          wid="100%"
        />
      </SearchHeader>

      <ResultList id="userList">
        {multiple && (
          <Col wid="100%" noFlex gap="8px" pad="8px">
            <SegmentControl
              options={[t('common.fan'), t('common.athlete')]}
              small
              textStyle={{
                b2: true
              }}
              selectedIdx={selectedOpt}
              handleSelect={setSelectedOpt}
              marg="0 0 8px"
            />

            {selected.map((x) => (
              <ResultRow key={x._id} onClick={() => handleClick(x)}>
                <Avatar user={x} small />
                <Text b1 med wid="100%" marg="0 0 0 2px">
                  {x.name}
                </Text>
                <Icon type="close" col={theme.GREY_60} dimensions={12} />
              </ResultRow>
            ))}

            <Text h2 marg="8px 0 0">
              {t('add.recent')}
            </Text>
          </Col>
        )}

        {visibleResults.map((x) => {
          const isSelected = !multiple && !!selected && x.name === selected;

          return (
            <ResultRow key={x._id} $selected={isSelected} onClick={() => handleClick(x)}>
              <Avatar user={x} small />
              <Text b1 med={!isSelected} bold={isSelected} wid="100%" marg="0 0 0 2px">
                {x.name}
              </Text>
              {isSelected && <Icon type="check" col={theme.SECONDARY} dimensions={16} />}
            </ResultRow>
          );
        })}

        {loading && !data.length && (
          <EmptyState>
            <Spinner2 col={theme.SECONDARY} />
          </EmptyState>
        )}

        {!loading && !visibleResults.length && (
          <EmptyState>
            <Icon type="search" col={theme.GREY_30} dimensions={28} />
            <Text b2 col={theme.GREY_50} center>
              {t('add.noUserFound')}
            </Text>
          </EmptyState>
        )}
      </ResultList>
    </Card>
  );

  if (inline) {
    return content;
  }

  return (
    <Row wid="100%" pos="relative">
      <Dropdown
        title={title}
        onClick={() => setVisible(!visible)}
        selected={multiple ? !!selected.length && `${selected.length} people` : selected}
      />

      <Modal
        visible={visible}
        wid="400px"
        modalBg="transparent"
        closeModal={() => setVisible(false)}
      >
        {content}
      </Modal>
    </Row>
  );
};

export default SearchUsers;
